import { Close } from '@mui/icons-material'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material'
import { memo, useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { TimeBucket_TimeSeries, timeBucket_TimeSeriesToJSON } from '../../proto/generated/common'
import { SuperChartConfiguration_TimeBucket } from '../../proto/generated/superchart-page'
import { RootState } from '../../store'

import {useStyles} from './styles'
import { selectMultiPaneFullScreen } from '../../selectors/common'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../assets/constants'
import DraggableDialogTitle from '../common/DraggableDialogTitle'
import { updateUserPreferences } from '../../services/apis/user'
import { selectCustomTimeBuckets } from '../../selectors/user'

type Props = {
    open: boolean
    handleClose: Function
    handleChangeBucket: (bucket: SuperChartConfiguration_TimeBucket) => void
}

const TimeSeriesOptions: Array<{label: string, value: TimeBucket_TimeSeries}> = [
    {
        label: 'Second',
        value: TimeBucket_TimeSeries.SECOND
    },
    {
        label: 'Minute',
        value: TimeBucket_TimeSeries.MINUTE
    },
    {
        label: 'Hour',
        value: TimeBucket_TimeSeries.HOUR
    },
    {
        label: 'Day',
        value: TimeBucket_TimeSeries.DAY
    },
    {
        label:'Week',
        value:TimeBucket_TimeSeries.WEEK
    },
     {
        label:'Month',
        value:TimeBucket_TimeSeries.MONTH
    },


]

const CustomTimeBucketModal: React.FC<Props> = ({ open, handleClose, handleChangeBucket }) => {
    const { classes } = useStyles();
    const container = useSelector(selectMultiPaneFullScreen)
    const [selectedInterval, setSelectedInterval] = useState(TimeSeriesOptions[0].value)
    const [selectedValue, setSelectedValue] = useState<string>('')
    const [error, setError] = useState('')
    const timeBuckets: SuperChartConfiguration_TimeBucket[] = useSelector((state: RootState) => state.chartStore.timeBuckets)
    const customTimeBuckets = useSelector(selectCustomTimeBuckets)

    const addTimeBucket = useCallback(() => {
        let newCustomTimeBuckets = [...customTimeBuckets]
        const bucket = {
            code: `${selectedValue}_${timeBucket_TimeSeriesToJSON(selectedInterval)}`,
            name: `${selectedValue} ${TimeSeriesOptions.find(t => t.value === selectedInterval)?.label.toLowerCase()}${Number(selectedValue) > 1 ? 's' : ''}`,
            timeSeries: selectedInterval,
            timeValue: Number(selectedValue)
        }
        if (newCustomTimeBuckets?.length && newCustomTimeBuckets?.length < 3) {
            newCustomTimeBuckets.push(bucket)
        } else if (newCustomTimeBuckets?.length && newCustomTimeBuckets?.length >= 3) {
            while (newCustomTimeBuckets?.length >= 3) {
                newCustomTimeBuckets?.shift()
            }
            newCustomTimeBuckets.push(bucket)
        } else {
            newCustomTimeBuckets = [bucket]
        }
        updateUserPreferences({
            body: {
                customTimeBuckets: newCustomTimeBuckets
            }
        });
        handleChangeBucket(bucket)
        handleClose()
    }, [selectedValue, selectedInterval, handleChangeBucket, handleClose, customTimeBuckets])

    useEffect(() => {
        const bucket = [...timeBuckets, ...customTimeBuckets].find(t => t.timeSeries === selectedInterval && t.timeValue === Number(selectedValue))
        if (bucket) {
            setError('Selected interval exists.')
        } else if (selectedInterval === TimeBucket_TimeSeries.SECOND && Number(selectedValue) > 59) {
            setError('Please enter a value less than 60.')
        } else if (selectedInterval === TimeBucket_TimeSeries.MINUTE && Number(selectedValue) > 59) {
            setError('Please enter a value less than 60.')
        } else if (selectedInterval === TimeBucket_TimeSeries.HOUR && Number(selectedValue) > 6) {
            setError('Please enter a value less than 7')
        } else if (selectedInterval === TimeBucket_TimeSeries.DAY && Number(selectedValue) > 9) {
            setError('Please enter a value less than 10')
        } else if (selectedValue.length > 4) {
            setError('Interval value is too big, please try again')
        } else if(Number(selectedValue)<=0){
            setError('Interval value must be greater than 0')
        } else {
            setError('')
        }
    }, [selectedInterval, selectedValue])

    const closeModal = useCallback(() => {
        handleClose()
    }, [handleClose])

    return (
        <Dialog
            open={open}
            maxWidth="xs"
            fullWidth
            container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
            onClose={closeModal}
            
        >
            <DraggableDialogTitle>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    <Typography variant={'h6'}> Add custom interval</Typography>
                    <IconButton
                        edge="end"
                        onClick={closeModal}
                        aria-label="close"
                    >
                        <Close />
                    </IconButton>
                </Stack>
            </DraggableDialogTitle>
            <DialogContent>
                <Stack direction={'row'} alignItems={'center'} sx={{ marginBottom: '10px' }} justifyContent={'space-between'}>
                    <Typography color="inherit" className={classes.label}>Type</Typography>
                    <div className={classes.value}>
                        <Select
                            size="small"
                            fullWidth
                            value={selectedInterval}
                            onChange={(e) => setSelectedInterval(Number(e.target.value))}
                        >
                            {
                                TimeSeriesOptions.map((option, i) => <MenuItem key={i}
                                                                            value={option.value}>{option.label}</MenuItem>)
                            }
                        </Select>
                    </div>
                </Stack>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    <Typography color="inherit" className={classes.label}>Interval</Typography>
                    <div className={classes.value}>
                        <TextField
                            value={selectedValue}
                            size="small"
                            type="number"
                            fullWidth
                            onChange={(e) => {
                                const newValue = e.target.value
                                if (newValue === '' || (/^[0-9]+$/).test(newValue)) {
                                    setSelectedValue(newValue)
                                }
                            }}
                            error={!!error}
                            inputProps={{
                                step: 1,
                                min: 0,
                                inputMode: 'numeric',
                                pattern: '[0-9]*'
                            }}
                        />
                        {error && <Typography color="error" variant="caption" className={classes.error}>{error}</Typography>}
                    </div>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" color="primary" onClick={() => handleClose()}>Cancel</Button>
                <Button variant="contained" color="primary" disabled={(!!error) || (!selectedValue)}
                        onClick={addTimeBucket}>Add</Button>
            </DialogActions>
        </Dialog>
    );
}

export default memo(CustomTimeBucketModal)